import React, { useContext, useMemo, useRef } from 'react';
import { Box, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { SizeMeProps } from 'react-sizeme';
import classNames from 'classnames';
import {
  ContactSchema,
  RoomCollaboratorSchema,
  RoomJSONStructureItem,
  RoomSchema,
  RoomStructureContent as RoomStructureContentSchema,
  RoomStructureHeader,
} from '../../../definitions/schemas';
import { composeUsername } from '../../../lib/DisplayName';
import {
  DOCUMENTS_LAZY_LOADING_MOBILE_THRESHOLD,
  DOCUMENTS_LAZY_LOADING_THRESHOLD,
} from '../../../enums/Constraints';
import { EventProperty } from '../../../tools/Analytics';
import {
  applyFolderChangesOverride,
  FolderChangesOverrideBindings,
  getCurrentItemsInStructure,
  getDocumentsCount,
  matchStructureItems,
} from '../../../lib/helper/RoomStructureHelper';
import {
  getContentModeFromLayout,
  getSectionHeadersModeFromLayout,
  isTilesLayout,
  RoomLayout,
  RoomSectionHeadersMode,
} from '../../../enums/RoomLayout';
import {
  RoomContentTypes,
  RoomStructureItemType,
} from '../../../enums/RoomStructureItemType';
import {
  buildRoomObjectAclsFromContentIds,
  getObjectAclsForContent,
} from '../../Content/ContentAccess/helper';
import ContentAccessList from '../../Content/ContentAccess/ContentAccessList';
import { trackDsrDeepLinkAccessed } from '../../../lib/helper/dsrDeepLinkAnalytics';
import { useBuyerAssetCopyLink } from '../../../pages/Room/View/useBuyerAssetCopyLink';
import { getConversationAuthor } from '../../Conversation/helper';
import { isCurrentUserCollaborator } from './helper';
import { isInternalRoom } from '../../../enums/RoomAudience';
import { orderSignables } from '../../Signables/helper';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';
import { StorageProviderMTPickers } from '../../../enums/Integrations/StorageProviders';
import { QUERY_STRING } from '../../../enums/QueryString';
import { RoomContentContextProps } from '../../../lib/RoomContentContext';
import { RoomSection } from '../../../enums/RoomAction';
import { RoomType } from '../../../enums/RoomTypes';
import { RoomViewContext } from '../View/RoomViewContext';
import { RoomWrapperApi } from './types';
import { useI18n } from '../../../lib/hook/useI18n';
import ConversationButton from '../components/Button/Conversation';
import ListCollaborators from '../components/Collaborators/ListCollaborators';
import RoomActionPlansWrapper, {
  PickedActionPlanEditorProps,
} from '../components/ActionPlans/RoomActionPlansWrapper';
import RoomAppBar from '../../Rooms/RoomAppBar';
import RoomBanner from '../components/RoomBanner';
import RoomBusinessCard from '../components/RoomBusinessCard';
import RoomHeaders from '../components/Headers/RoomHeaders';
import RoomHeadersMenuList from '../components/Headers/RoomHeadersMenuList';
import RoomName from '../components/RoomName';
import RoomSalesActions from '../components/RoomSalesActions';
import RoomSignablesWrapper, {
  RoomSignablesWrapperProps,
} from '../components/Signables/RoomSignablesWrapper';
import buildViewStructureNodes from '../components/buildViewStructureNodes';
import FolderViewHeader from '../../Folders/FolderViewHeader';
import FolderViewMessage from '../../Folders/FolderViewMessage';
import ScheduleCall from '../components/CommunicationActions/ScheduleCall';
import ShareRoom from '../components/Button/ShareRoom';
import TheaterModeContextProvider from '../components/TheaterMode/TheaterModeContext';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';
import SnackbarStore from '../../../stores/observables/SnackbarStore';
import UserStore from '../../../stores/observables/UserStore';
import WelcomeMessage from '../components/WelcomeMessage/WelcomeMessage';
import { getConsent } from '../../../lib/Consent';
import { CONSENT } from '../../../enums/RoomConsent';
import {
  trackDsrFolderBreadcrumbClicked,
  trackDsrFolderOpenedRoomVisitor,
} from '../../../lib/helper/folderAnalytics';
import { FolderData } from '../../../pages/Folder/View/useFolderData';

type DeepLinkSectionResolution =
  | { row: RoomStructureContentSchema; type: 'asset' }
  | { type: 'header' }
  | { type: 'none' };

function resolveDeepLinkSection(
  sectionId: string,
  structureHeaders: RoomStructureHeader[],
  structureContents: RoomStructureContentSchema[]
): DeepLinkSectionResolution {
  if (structureHeaders.some((h) => h.id === sectionId)) {
    return { type: 'header' };
  }
  const row = structureContents.find((c) => c.id === sectionId);
  if (row && RoomContentTypes.includes(row.type)) {
    return { row, type: 'asset' };
  }
  return { type: 'none' };
}

const useStyles = makeStyles((theme: Theme) => ({
  appBarWide: {
    // 1240px
    maxWidth: theme.spacing(155),
  },
  roomBannerRounded: {
    borderRadius: theme.spacing(1, 1, 0, 0),
    overflow: 'hidden',
  },
}));

export type PickedRoomSignablesWrapperProps = Pick<
  RoomSignablesWrapperProps,
  | 'disableChangesOnScroll'
  | 'disableSign'
  | 'disableStickyPill'
  | 'downloadSignableId'
  | 'onSignableSigned'
  | 'scrollThresholdBase'
>;

interface Props {
  actionable?: boolean;
  ActionPlanEditorProps: PickedActionPlanEditorProps;
  children: (api: RoomWrapperApi) => JSX.Element;
  collaborators: RoomCollaboratorSchema[];
  contextProps: RoomContentContextProps;
  /**
   * Complete pool of openable room contents (root + all folder-nested),
   * populated only once the copilot is opened. Merged into the theater-mode
   * pool as an additive source so a copilot @-mention can open a content nested
   * in a folder that isn't currently in view. Not required for tile clicks,
   * which are covered by the root structure + `folderViewData`.
   */
  copilotContentPool?: RoomStructureContentSchema[];
  currentFolderId?: string | null;
  /**
   * When provided, overrides the root-level folder list's itemCount/coverSigned
   * from in-memory folderChanges edit state, so unsaved local edits (e.g. a
   * newly created folder with locally added content) are reflected before the
   * room is saved. Only the room preview passes this — the live room view has
   * no such thing as unsaved local edits.
   */
  folderStateBindings?: FolderChangesOverrideBindings;
  folderViewData?: FolderData;
  disabled?: boolean;
  disableWelcomeMessageOverlay?: boolean;
  hideContentTitles?: boolean;
  initialConversationMessage?: string | null;
  isContactVisitor?: boolean;
  onContactEdited?: (updatedContact?: ContactSchema) => void;
  onConversationDialogOpened?: () => void;
  onConversationDialogClosed?: () => void;
  onConversationMessageConsumed?: () => void;
  onFolderBack?: () => void;
  onFolderOpen?: (folderId: string) => void;
  referrer: string;
  renderContentAcl?: (
    structureContent: RoomJSONStructureItem
  ) => React.ReactNode;
  room: RoomSchema | undefined;
  roomType: RoomType;
  sellingCollaborators: RoomCollaboratorSchema[];
  SignablesProps?: PickedRoomSignablesWrapperProps;
  trackingProperties: EventProperty;
  userContact?: ContactSchema;
  isBuyerCopilotEnabledForRoom?: boolean;
}

const RoomViewController: React.FC<Props> = (props: Props): JSX.Element => {
  const { isGuestVisitor, queryStringParams } = useContext(RoomViewContext);

  const { t } = useI18n();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const areSmsNotificationsEnabled = useFeatureFlag(
    OrganizationFlagEnum.SMS_NOTIFICATIONS
  );

  const areFoldersEnabled = useFeatureFlag(
    OrganizationFlagEnum.RELEASE_CONTROL_DSR_FOLDERS
  );

  const isConsentDeclinedSuppression = useFeatureFlag(
    OrganizationFlagEnum.CONSENT_DECLINED_SUPPRESSION
  );

  const {
    actionable = false,
    ActionPlanEditorProps,
    children,
    collaborators,
    contextProps,
    copilotContentPool,
    currentFolderId,
    folderStateBindings,
    folderViewData,
    disabled,
    disableWelcomeMessageOverlay = false,
    hideContentTitles = false,
    initialConversationMessage,
    isContactVisitor = false,
    onContactEdited,
    onConversationDialogOpened,
    onConversationDialogClosed,
    onConversationMessageConsumed,
    onFolderBack,
    onFolderOpen,
    referrer,
    renderContentAcl,
    room,
    roomType,
    sellingCollaborators,
    SignablesProps,
    trackingProperties,
    userContact,
    isBuyerCopilotEnabledForRoom,
  } = props;

  const isInlineIframeViewerEnabled = useFeatureFlag(
    OrganizationFlagEnum.INLINE_IFRAME_VIEWER
  );

  const contentMode = useMemo(
    () => getContentModeFromLayout(room?.layout),
    [room]
  );

  const sectionHeadersMode = useMemo(
    () => getSectionHeadersModeFromLayout(room?.layout),
    [room]
  );

  const [structureHeaders, structureContents, rawStructureFolders] = useMemo(
    () =>
      matchStructureItems(
        room?.contents || [],
        room?.structure,
        isInlineIframeViewerEnabled
      ),
    [room, isInlineIframeViewerEnabled]
  );

  const structureFolders = useMemo(
    () => applyFolderChangesOverride(rawStructureFolders, folderStateBindings),
    [rawStructureFolders, folderStateBindings]
  );

  // Pool of contents openable in theater mode, merged (deduped by id) from
  // three sources with decreasing reliability:
  //   1. `structureContents` — the root structure, always present.
  //   2. `folderViewData` — the currently-open folder's contents. A tile click
  //      can only ever target the root or the folder in view, so (1)+(2) fully
  //      cover tile clicks with no dependency on the copilot's lazy fetch.
  //   3. `copilotContentPool` — the complete root+all-folders pool, populated
  //      only once the copilot is opened. This lets a copilot @-mention open a
  //      content nested in a folder that isn't currently in view. OPEN_CONTENT
  //      only fires while the copilot is open, so the pool is always populated
  //      by the time that path needs it.
  const allRoomContents = useMemo(() => {
    const merged = [...structureContents];
    const seenIds = new Set(structureContents.map((content) => content.id));
    const addUnique = (contents: RoomStructureContentSchema[]) => {
      contents.forEach((content) => {
        if (!seenIds.has(content.id)) {
          seenIds.add(content.id);
          merged.push(content);
        }
      });
    };
    addUnique(folderViewData?.structureContents ?? []);
    addUnique(copilotContentPool ?? []);
    return merged;
  }, [structureContents, folderViewData, copilotContentPool]);

  const structureItems = useMemo(
    () =>
      room?.contents && room?.structure
        ? getCurrentItemsInStructure(room.contents, room.structure)
        : [],
    [room]
  );

  const orderedSignables = useMemo(
    () => orderSignables(room?.signables || [], room?.signablesStructure || []),
    [room]
  );
  /**
   * Avoid additional measures to optimize room's documents loading (lazy
   * loading those documents only when they're within viewport) only for
   * scenarios when there are too many of them in the room that cause a
   * significant performance impact.
   *
   * For mobile devices, the threshold is lower, as there are usually less
   * resources available. If too many docs are rendered at the same time,
   * the page could crash. Check the attached ticket for more context.
   *
   * @see https://mindtickle.atlassian.net/browse/DSR-1363
   */
  const enableDocumentViewportDetection = useMemo(() => {
    const threshold = isMobile
      ? DOCUMENTS_LAZY_LOADING_MOBILE_THRESHOLD
      : DOCUMENTS_LAZY_LOADING_THRESHOLD;

    return getDocumentsCount(structureContents) > threshold;
  }, [isMobile, structureContents]);

  /**
   * This is for "Share Room" and "Schedule a Call". The actionable flag
   * is used for the contact's view of the room and when it's turned off,
   * by the room/room template previews for example, it makes it so those actions
   * don't do anything. Both actions are available for Enable Us users
   * (admins/salespeople) who visit one of the rooms in their org.
   */
  const areRoomActionsDisabled = useMemo(
    () => !actionable || !(room?.id || userContact?.id),
    [actionable, room, userContact]
  );

  const isTrackingEnabled = isConsentDeclinedSuppression
    ? getConsent()?.consent === CONSENT.ACCEPTED
    : isGuestVisitor;

  const trackingProps = useMemo(
    () =>
      !room
        ? undefined
        : {
            contactId: userContact?.id,
            roomId: room.id,
            track: isTrackingEnabled,
          },
    [isTrackingEnabled, room, userContact]
  );

  const isContentLink = useMemo(
    () => roomType === RoomType.ContentLink,
    [roomType]
  );

  const isConversationEnabled = useMemo(
    () => !!room?.enableQa && !isContentLink,
    [room, isContentLink]
  );

  const isShareRoomEnabled = useMemo(
    () => !!room?.enableShare && !isContentLink && !isInternalRoom(room),
    [room, isContentLink]
  );

  const { current: sectionHeaderRefs } = useRef(
    new Map<string, HTMLDivElement>()
  );

  const { current: deepLinkSection } = useRef<string | undefined>(
    queryStringParams && queryStringParams[QUERY_STRING.SECTION]
  );

  const onCopyAssetLink = useBuyerAssetCopyLink({
    room,
    structureContents,
  });

  const folderStructure: FolderData = folderViewData ?? {
    folder: undefined,
    loading: false,
    error: false,
    structureItems: [],
    structureContents: [],
    structureHeaders: [],
    structureFolders: [],
    breadcrumbs: [],
  };

  const theaterDeepLinkSectionId = useMemo(() => {
    const id = deepLinkSection;
    if (!id) {
      return undefined;
    }
    const resolved = resolveDeepLinkSection(
      id,
      structureHeaders,
      structureContents
    );
    if (resolved.type !== 'asset') {
      return undefined;
    }
    const { row } = resolved;
    if (
      isTilesLayout(room?.layout) ||
      row.match?.mtData?.provider === StorageProviderMTPickers.AssetHub ||
      row.match?.mtData?.provider === StorageProviderMTPickers.CallAi
    ) {
      return id;
    }
    return undefined;
  }, [deepLinkSection, room?.layout, structureContents, structureHeaders]);

  const invalidDeepLinkTargetSnackKeyRef = useRef<string | null>(null);
  const sectionDeepLinkAccessTrackKeyRef = useRef<string | null>(null);
  const deepLinkTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const hasSignables = orderedSignables.length > 0;

  const isStory3Columns = room?.layout === RoomLayout.Story3Columns;
  const isSingleContent = room?.layout === RoomLayout.SingleContent;
  const isTiles3Columns = room?.layout === RoomLayout.Tiles3Columns;

  /**
   * Adds the element from a section header into the given refs parameter.
   * Since the refs are objects, we can alter them, pushing the target
   * section header and the HTML element for its title into this refs array,
   * which can be used for scrolling the element when a title is clicked.
   */
  const assignRef = (id: string) => (el: HTMLDivElement) => {
    sectionHeaderRefs.set(id, el);
    sectionDeepLink();
  };

  const sectionDeepLink = () => {
    if (!(deepLinkSection && sectionHeaderRefs)) {
      return;
    }

    const resolved = resolveDeepLinkSection(
      deepLinkSection,
      structureHeaders,
      structureContents
    );
    const structureReady = Boolean(room?.id) && room?.structure?.items != null;

    if (structureReady && resolved.type === 'none' && room) {
      const missingSignature = `missing:${room.id}:${deepLinkSection}`;
      if (invalidDeepLinkTargetSnackKeyRef.current !== missingSignature) {
        invalidDeepLinkTargetSnackKeyRef.current = missingSignature;
        SnackbarStore.show(t('rooms.editor.section_header.target_missing'));
      }
      return;
    }

    const ref = sectionHeaderRefs.get(deepLinkSection);
    if (!ref) {
      return;
    }

    if (structureReady && resolved.type !== 'none' && room) {
      const signature = `${room.id}:${deepLinkSection}`;
      if (sectionDeepLinkAccessTrackKeyRef.current !== signature) {
        sectionDeepLinkAccessTrackKeyRef.current = signature;
        trackDsrDeepLinkAccessed({
          authRequired: !!(
            room.requiresEmail ||
            room.verifyEmail ||
            room.requiresPasscode
          ),
          roomId: String(room.id),
          targetId: deepLinkSection,
          targetType: resolved.type === 'header' ? 'section' : 'asset',
        });
      }
    }

    if (deepLinkTimeoutRef.current) {
      clearTimeout(deepLinkTimeoutRef.current);
    }

    deepLinkTimeoutRef.current = setTimeout(() => {
      ref.scrollIntoView(true);
    }, 1000);
  };

  const onSectionHeaderClicked = (item: RoomStructureHeader) => {
    if (!sectionHeaderRefs) {
      return;
    }

    const ref = sectionHeaderRefs.get(item.id);

    if (!ref) {
      return;
    }

    // let the dom be ready for scrolling after closing the drawer menu
    setTimeout(() => {
      ref.scrollIntoView({
        behavior: 'smooth',
      });
    }, 350);
  };

  const renderShareRoom = (
    isMenuItem: boolean,
    isFloatingActionButton = false
  ): JSX.Element | undefined => {
    if (!(room && isShareRoomEnabled)) {
      return undefined;
    }

    const title = t(
      areSmsNotificationsEnabled ? 'share.modal.title_sms' : 'share.modal.title'
    );

    return (
      <ShareRoom
        actionLabel={t('share.modal.button')}
        contactId={userContact?.id}
        disabled={areRoomActionsDisabled}
        isFloatingActionButton={isFloatingActionButton}
        isMenuItem={isMenuItem}
        label={isGuestVisitor ? 'peer' : 'contact'}
        roomId={room.id}
        title={title}
        trackingProperties={trackingProperties}
      />
    );
  };

  const renderRoomConversation = (
    isMenuItem: boolean,
    isQuickAccessIcon = false,
    onClose?: () => void
  ): JSX.Element | undefined => {
    if (!(room && isConversationEnabled)) {
      return undefined;
    }

    const currentUser = UserStore.get();
    const isCollaborator = isCurrentUserCollaborator(collaborators);
    const isOwner = currentUser?.id === room?.owner?.id;
    const conversationOwner = userContact || currentUser;

    return (
      <ConversationButton
        author={getConversationAuthor(userContact)}
        conversationOwnerId={conversationOwner?.id}
        initialComposerMessage={initialConversationMessage}
        initialOpen={deepLinkSection === RoomSection.QA}
        isCollaborator={isCollaborator}
        isContactVisitor={isContactVisitor}
        isGuestVisitor={isGuestVisitor}
        isMenuItem={isMenuItem}
        isOwner={isOwner}
        isQuickAccessIcon={isQuickAccessIcon}
        messagesCount={room.conversationThreadsCount || 0}
        onClose={onClose}
        onConversationDialogOpened={onConversationDialogOpened}
        onConversationDialogClosed={onConversationDialogClosed}
        onContactEdited={onContactEdited}
        onConversationMessageConsumed={onConversationMessageConsumed}
        referrer={referrer}
        roomId={room.id}
        roomMessageId={queryStringParams?.[QUERY_STRING.ROOM_MESSAGE_ID]}
        roomUUID={room.uuid}
        trackingProperties={trackingProperties}
        userContactId={userContact?.id}
      />
    );
  };

  const renderAppBarActions = () => (
    <Box alignItems="center" display="flex" ml={4}>
      <Box mr={isShareRoomEnabled ? 3 : 0}>{renderRoomConversation(false)}</Box>
      {renderShareRoom(false)}
    </Box>
  );

  const renderAppBarPrimaryAction = () => {
    if (isSingleContent || disabled) {
      return;
    }

    return isMobile ? renderMobileMenu() : renderAppBarActions();
  };

  const renderAppBar = (): JSX.Element => (
    <RoomAppBar
      centeredLogos={isContentLink || disabled}
      className={classNames({
        [classes.appBarWide]: isStory3Columns || isTiles3Columns,
      })}
      clientLogo={room?.clientLogoUrl || room?.clientLogoUri}
      navigation={renderNavigation()}
      organization={room?.organization}
      primaryAction={renderAppBarPrimaryAction()}
    />
  );

  const renderSectionHeaders = (
    exclusiveModeToBeRendered: RoomSectionHeadersMode
  ): JSX.Element | undefined => {
    if (exclusiveModeToBeRendered !== sectionHeadersMode) {
      return undefined;
    }

    return !structureHeaders.length ? undefined : (
      <RoomHeaders
        mode={sectionHeadersMode}
        onClick={onSectionHeaderClicked}
        structureHeaders={structureHeaders}
      />
    );
  };

  const renderMobileMenu = () => (
    <RoomHeadersMenuList
      AppBarProps={{
        centeredLogos: isContentLink,
        className: classNames({
          [classes.appBarWide]:
            room?.layout === RoomLayout.Story3Columns ||
            room?.layout === RoomLayout.Tiles3Columns,
        }),
        clientLogo: room?.clientLogoUrl || room?.clientLogoUri,
        organization: room?.organization,
      }}
      // open menu in case of deeplink for conversation drawer
      initialOpen={
        deepLinkSection === RoomSection.QA ||
        queryStringParams?.[QUERY_STRING.ROOM_MESSAGE_ID]
      }
      onClick={onSectionHeaderClicked}
      renderRoomConversation={renderRoomConversation}
      renderShareRoom={renderShareRoom}
      structureHeaders={structureHeaders}
    />
  );

  const renderNavigation = () => {
    if (isMobile || isStory3Columns || isSingleContent || isTiles3Columns) {
      return <></>;
    }

    return !structureHeaders.length ? undefined : (
      <RoomHeaders
        mode={RoomSectionHeadersMode.Tabs}
        onClick={onSectionHeaderClicked}
        structureHeaders={structureHeaders}
      />
    );
  };

  const renderBanner = (): JSX.Element | null =>
    !room?.bannerUri ? null : (
      <RoomBanner
        className={classes.roomBannerRounded}
        image={room.bannerUri}
      />
    );

  const renderActionPlans = (): JSX.Element | undefined =>
    !room?.actionPlans?.length ? undefined : (
      <Box
        sx={(theme) => ({
          mb: hasSignables ? undefined : 6,
          mt: -1,
          top: theme.spacing(2 + (orderedSignables?.length || 0) * 10),
          zIndex: 2,
        })}
      >
        <RoomActionPlansWrapper
          ActionPlanEditorProps={ActionPlanEditorProps}
          referrer={referrer}
          room={room}
          trackingProperties={trackingProperties}
          userContactId={userContact?.id}
        />
      </Box>
    );

  const renderSignables = (): JSX.Element | undefined => {
    if (!hasSignables) {
      return undefined;
    }

    return (
      <RoomSignablesWrapper
        {...SignablesProps}
        referrer={referrer}
        roomUuid={room?.uuid}
        signables={orderedSignables}
        userContactId={userContact?.id}
      />
    );
  };

  // Opening a folder tile in the read-only room view. The parent is the folder
  // currently in view (or the room root when opening from the top level).
  const handleFolderOpen = (folderId: string) => {
    if (areFoldersEnabled) {
      trackDsrFolderOpenedRoomVisitor({
        roomId: room?.id,
        roomType,
        folderId,
        parentFolderId: currentFolderId ?? undefined,
      });
    }
    onFolderOpen?.(folderId);
  };

  // Clicking a breadcrumb in the folder header. The parent is the crumb
  // immediately preceding the clicked one in the trail (undefined at root).
  const handleBreadcrumbNavigate = (folderId: string) => {
    if (areFoldersEnabled) {
      const crumbs = folderStructure.breadcrumbs ?? [];
      const clickedIndex = crumbs.findIndex((crumb) => crumb.id === folderId);
      const parentFolderId =
        clickedIndex > 0 ? crumbs[clickedIndex - 1].id : undefined;
      trackDsrFolderBreadcrumbClicked({
        roomId: room?.id,
        roomType,
        folderId,
        parentFolderId,
        depth: clickedIndex >= 0 ? clickedIndex : undefined,
      });
    }
    onFolderOpen?.(folderId);
  };

  /**
   * Folder contents. Returned as a flat array (header row + content nodes) so
   * ContentLayout places each node in its own grid cell — identical to the
   * room path. Returning a single wrapping component would collapse everything
   * into one column.
   */
  const renderFolderContentAcl = (
    structureContent: RoomJSONStructureItem
  ): React.ReactNode => {
    if (!areFoldersEnabled || !room || isInternalRoom(room)) return;

    const { id, type } = structureContent;
    const isTextBox = type === RoomStructureItemType.TextBox;
    const objectAclsContents = buildRoomObjectAclsFromContentIds(
      folderStructure.roomObjectAclsContents
    );
    const contentObjectAcls = getObjectAclsForContent(
      id,
      type,
      objectAclsContents
    );

    if (!contentObjectAcls?.length) return;

    const contentAccessListProps = {
      contentId: structureContent.id,
      contextProps,
      disablePortal: true,
      loadFromResource: true,
      objectAcls: contentObjectAcls,
      positionStyle: { zIndex: 1, left: isTextBox ? 2 : undefined },
      roomId: room.id,
      tileMode: true,
    };

    return <ContentAccessList {...contentAccessListProps} />;
  };

  const renderFolderContents = (
    currentRoom: RoomSchema,
    sizeMeProps: SizeMeProps
  ): React.ReactNode => {
    if (folderStructure.loading) {
      return <FolderViewMessage fullWidth loading />;
    }

    if (folderStructure.error || !folderStructure.folder) {
      return (
        <FolderViewMessage
          fullWidth
          message="Unable to load folder contents."
        />
      );
    }

    const header = (
      <FolderViewHeader
        fullWidth
        key="folder-header"
        name={folderStructure.folder!.name}
        onBack={() => onFolderBack?.()}
        breadcrumbs={folderStructure.breadcrumbs}
        onNavigate={handleBreadcrumbNavigate}
      />
    );

    if (folderStructure.structureItems.length === 0) {
      return [
        header,
        <FolderViewMessage
          key="folder-empty"
          fullWidth
          message="This folder is empty."
        />,
      ];
    }

    return [
      header,
      ...buildViewStructureNodes({
        structureItems: folderStructure.structureItems,
        structureContents: folderStructure.structureContents,
        structureHeaders: folderStructure.structureHeaders,
        structureFolders: folderStructure.structureFolders,
        contentMode,
        contextProps,
        referrer,
        roomId: currentRoom.id,
        sizeMeProps,
        trackingProperties,
        isBuyerCopilotEnabledForRoom,
        keyPrefix: 'folder-structure',
        onFolderClick: handleFolderOpen,
        renderContentAcl: renderFolderContentAcl,
        TrackingProps: trackingProps,
      }),
    ];
  };

  const renderContents = (sizeMeProps: SizeMeProps) => {
    if (!room) {
      return <></>;
    }

    if (currentFolderId) {
      return renderFolderContents(room, sizeMeProps);
    }

    // this is the scenario where the content from the content link has
    // been deleted from the org's library and it's not available to view
    if (isSingleContent && structureContents.length === 0) {
      return (
        <Box py={8} textAlign="center">
          <Typography color="textPrimary" variant="body1">
            This content is not available anymore.
          </Typography>
          <Typography color="textSecondary" variant="body2">
            Please contact the person who shared it for access.
          </Typography>
        </Box>
      );
    }

    return buildViewStructureNodes({
      structureItems,
      structureContents,
      structureHeaders,
      structureFolders,
      contentMode,
      contextProps,
      referrer,
      roomId: room.id,
      sizeMeProps,
      trackingProperties,
      isBuyerCopilotEnabledForRoom,
      keyPrefix: 'structure',
      onFolderClick: handleFolderOpen,
      assignRef,
      hideHeader: isSingleContent,
      onCopyAssetLink,
      theaterDeepLinkSectionId,
      TrackingProps: trackingProps,
      actionable,
      enableDocumentViewportDetection,
      hideContactInformation: hideContentTitles,
      renderContentAcl,
    });
  };

  const renderName = (): JSX.Element | null =>
    !room?.name ? null : (
      <RoomName
        contact={room.contact}
        deal={room.deal}
        name={room.name}
        variant={room?.bannerUri ? 'h6' : 'h5'}
      />
    );

  const renderWelcomeMessage = (): JSX.Element | undefined => {
    const isTextContent = room?.welcomeMessage?.type === 'text';
    return !room?.welcomeMessage?.id ? undefined : (
      <WelcomeMessage
        disableWelcomeMessageOverlay={disableWelcomeMessageOverlay}
        owner={room.enableContactCard ? room.owner : undefined}
        referrer={referrer}
        roomId={room.id}
        track={isGuestVisitor && !isTextContent}
        userContactId={userContact?.id}
        welcomeMessage={room.welcomeMessage}
      />
    );
  };

  const renderBusinessCard = (): JSX.Element | undefined => {
    if (!room?.enableContactCard) {
      return;
    }
    const user = room?.owner || room?.createdBy;
    if (!(room && user) || disabled) {
      return undefined;
    }

    if (!sellingCollaborators.length) {
      return (
        <RoomBusinessCard
          cardBorder
          enableBody
          enableHeader
          hideEmail={areSmsNotificationsEnabled}
          owner={user}
          readOnly
          username={composeUsername(user)}
        />
      );
    }

    return (
      <ListCollaborators
        clientLogo={room.clientLogoUrl || room.clientLogoUri}
        hideEmail={areSmsNotificationsEnabled}
        organization={room.organization!}
        owner={user}
        readOnly
        referrer={referrer}
        roomCollaborators={sellingCollaborators}
        roomUuid={room.uuid}
      />
    );
  };

  const renderSalesActions = (): JSX.Element | undefined =>
    !(room?.buttons?.length && room?.structure) ? undefined : (
      <RoomSalesActions
        actionable={actionable}
        referrer={referrer}
        roomId={room.id}
        salesActions={room.buttons}
        structure={room.structure}
        trackingProperties={trackingProperties}
        TrackingProps={trackingProps}
      />
    );

  const renderCommunicationActions = (): JSX.Element | undefined =>
    !room?.organization?.scheduleCall ? undefined : (
      <Box>
        <ScheduleCall
          contactId={userContact?.id}
          disabled={areRoomActionsDisabled}
          roomId={room.id}
          trackingProperties={trackingProperties}
        />
      </Box>
    );

  const renderWhenReady =
    (callback: (...args: any[]) => React.ReactNode) =>
    (...args: any[]) =>
      disabled ? null : callback(...args);

  return (
    <TheaterModeContextProvider
      contextProps={contextProps}
      referrer={referrer}
      structureContents={structureContents}
      allRoomContents={allRoomContents}
      TrackingProps={trackingProps}
      isBuyerCopilotEnabledForRoom={isBuyerCopilotEnabledForRoom}
    >
      {children({
        renderAppBar,
        renderBusinessCard,
        renderContents,
        renderActionPlans: renderWhenReady(renderActionPlans),
        renderBanner: renderWhenReady(renderBanner),
        renderCommunicationActions: renderWhenReady(renderCommunicationActions),
        renderName: renderWhenReady(renderName),
        renderSalesActions: renderWhenReady(renderSalesActions),
        renderSectionHeaders: renderWhenReady(renderSectionHeaders),
        renderSignables: renderWhenReady(renderSignables),
        renderWelcomeMessage: renderWhenReady(renderWelcomeMessage),
      })}
    </TheaterModeContextProvider>
  );
};

export default RoomViewController;
